import collect from 'collect.js';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Direction, NOPStrategySignalItem } from '../../../shared/types';
import { PriceDataPoint } from '../../../types';
import {
  ExternalMarkerGenerator,
  ExternalMarkerGeneratorGetMarkers,
} from './base';

interface TipsTooltipProps {
  NOPStrategySignalItems: NOPStrategySignalItem[];
}

export const getSignalTranslateCode = (signal: string) => {
  switch (signal) {
    case 'Buy': {
      return 'buy-signal';
    }
    case 'Sell': {
      return 'sell-signal';
    }
    default: {
      return 'flat-long-position';
    }
  }
};

const TipsTooltip: React.FC<TipsTooltipProps> = ({
  NOPStrategySignalItems,
}) => {
  const [t] = useTranslation();

  return (
    <div>
      {NOPStrategySignalItems.map((NOPStrategySignalItem, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div>
            <b>{t(getSignalTranslateCode(NOPStrategySignalItem.signal))}</b>
          </div>
        </div>
      ))}
    </div>
  );
};

const buySignalColor = '#51F34E';
const longSignalColor = '#fce303';
const sellSignalColor = '#fc552b';

const makeData = (
  direction: Direction,
  date: string,
  dataPoint: PriceDataPoint,
  width: number,
  previousDirection: Direction
) => {
  let coordPoint = dataPoint.high;
  let symbolRotate = 270;
  let symbolOffset: (string | number)[] = ['50%', '-100%'];
  let color = longSignalColor;
  if (direction === 'Buy') {
    coordPoint = dataPoint.low;
    symbolRotate = 0;
    symbolOffset = [0, '100%'];
    color = buySignalColor;
  } else if (direction === 'Sell') {
    coordPoint = dataPoint.high;
    symbolRotate = 180;
    symbolOffset = [0, '-100%'];
    color = sellSignalColor;
  } else if (
    (direction === 'Flat Long Position' || direction === 'Break_mdd') &&
    previousDirection === 'Buy'
  ) {
    coordPoint = dataPoint.high;
    symbolRotate = 180;
    symbolOffset = [0, '-100%'];
  } else if (
    (direction === 'Flat Long Position' || direction === 'Break_mdd') &&
    previousDirection === 'Sell'
  ) {
    coordPoint = dataPoint.low;
    symbolRotate = 0;
    symbolOffset = [0, '100%'];
  }

  return {
    name: direction,
    coord: [date, coordPoint],
    symbol:
      //This an SVG PathData the syntax is 'path://<SVG PathData>'
      //reference: https://echarts.apache.org/zh/option.html#series-line.markPoint.symbol
      //SVG PathData reference: https://fontawesomeicons.com/svg/icons/circle-down
      'path://M0 12.1094C0 5.41992 5.41992 0 12.1094 0C18.7988 0 24.2188 5.41992 24.2188 12.1094C24.2188 18.7988 18.7988 24.2188 12.1094 24.2188C5.41992 24.2188 0 18.7988 0 12.1094ZM7.01172 13.5205L10.5469 9.83398V18.75C10.5469 19.3994 11.0693 19.9219 11.7188 19.9219H12.5C13.1494 19.9219 13.6719 19.3994 13.6719 18.75V9.83398L17.207 13.5205C17.6611 13.9941 18.418 14.0039 18.8818 13.54L19.4141 13.0029C19.873 12.5439 19.873 11.8018 19.4141 11.3477L12.9395 4.86816C12.4805 4.40918 11.7383 4.40918 11.2842 4.86816L4.7998 11.3477C4.34082 11.8066 4.34082 12.5488 4.7998 13.0029L5.33203 13.54C5.80078 14.0039 6.55762 13.9941 7.01172 13.5205Z',
    symbolSize: [width, width],
    symbolRotate: symbolRotate,
    symbolOffset: symbolOffset,
    itemStyle: {
      color: color,
    },
  };
};

const filterSignal = (NOPStrategySignalItems: NOPStrategySignalItem[]) => {
  const itemInSignal = [];
  for (const strategySignalItem of NOPStrategySignalItems) {
    if (
      strategySignalItem.signal !== 'Hold' &&
      strategySignalItem.signal !== undefined
    ) {
      itemInSignal.push(strategySignalItem);
    }
  }
  return itemInSignal;
};
class NOPStrategySignal extends ExternalMarkerGenerator {
  NOPStrategySignalItems: NOPStrategySignalItem[];
  constructor(NOPStrategySignalItems: NOPStrategySignalItem[]) {
    super();
    this.NOPStrategySignalItems = NOPStrategySignalItems || [];
  }

  getMarkers(
    dataPoints: ReadonlyArray<PriceDataPoint>
  ): ExternalMarkerGeneratorGetMarkers {
    const signalWithCoordDate = filterSignal(this.NOPStrategySignalItems);
    // Since the strategy signal is updated at midnight,
    // there will be a situation where there is no stock price yet and we will
    // automatically switch the signal to the latest day.
    if (
      dataPoints?.at(-1)?.date &&
      signalWithCoordDate?.at(-1)?.date &&
      dataPoints?.at(-1)?.date < signalWithCoordDate?.at(-1)?.date
    ) {
      signalWithCoordDate.at(-1)['date'] = dataPoints.at(-1)['date'];
    }
    const signalByDate = collect(signalWithCoordDate).groupBy('date').all();
    const data = [];
    let previousDirection;
    for (const dataPoint of dataPoints) {
      const date = String(dataPoint.date);
      const signalOnDate: NOPStrategySignalItem[] = signalByDate[date]?.items;
      if (signalOnDate) {
        const directions = new Set(
          signalOnDate
            .map(({ signal }) => signal)
            .filter(direction => direction !== null && direction !== undefined)
        );
        for (const direction of directions) {
          data.push(
            makeData(direction, date, dataPoint, 20, previousDirection)
          );
          previousDirection = direction;
        }
      }
    }
    const tooltipCreater = (
      name: string, // signal
      coord: [string, number]
    ): React.ReactElement => {
      const [date] = coord;
      const signalOnDate: NOPStrategySignalItem[] = signalByDate[date]?.items;
      const signalMatchingDirection = signalOnDate.filter(
        ({ signal }) => signal === name
      );
      return React.createElement(TipsTooltip, {
        NOPStrategySignalItems: signalMatchingDirection,
      });
    };
    return { data, tooltipCreater };
  }
}

export default NOPStrategySignal;
